/* AddTeamModal — the WP Mgmt ▸ Teams "+ New Team" form in a popup, so the
 * Scheduler/AD can add a team from the Opponents page or straight from a game's
 * Opponent dropdown ("+ Add New"). Same ENTITIES.teams form, same
 * /api/ol-canonical/teams endpoint → same dataset, slug built by OL, WP push
 * enqueued by the backend. Doc: /app/docs/OPPONENTS_UI.md.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import EntityForm from '../admin/EntityForm';
import bridgeApi from '../admin/bridgeApi';
import { ENTITIES } from '../admin/entityConfigs';

const API = process.env.REACT_APP_BACKEND_URL;

/** Save a teams payload exactly like WP Mgmt (AdminManager.handleSave). */
export async function saveTeam(orgId, values) {
  const payload = { ...values };
  const olMeta = payload._ol_meta;
  delete payload._ol_meta;
  let saved;
  if (payload.id) {
    saved = await bridgeApi.patch('/teams', orgId, payload.id, payload);
  } else {
    saved = await bridgeApi.post('/teams', orgId, payload);
  }
  const row = saved?.data || saved || {};
  const id = row.id || payload.id;      // same key WP Mgmt uses for /team-meta
  if (olMeta && id && /^\d+$/.test(String(id))) {   // team-meta is keyed by the WP team id
    try {
      await axios.put(`${API}/api/team-meta/${orgId}/${id}`, {
        opponent_type: olMeta.opponent_type || '', is_conference: !!olMeta.is_conference, notes: olMeta.notes || '',
      });
    } catch (e) { console.warn('team-meta save failed', e); }
  }
  bridgeApi.invalidateEntity?.('/teams');
  return row;
}

export default function AddTeamModal({ open, orgId, initialTitle = '', onClose, onCreated }) {
  const [entities, setEntities] = useState({ sports: [], venues: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !orgId) return;
    setError('');
    Promise.all([
      bridgeApi.get('/sports', orgId, { limit: 200 }),
      bridgeApi.get('/venues', orgId, { limit: 1000 }),
    ]).then(([s, v]) => setEntities({
      sports: Array.isArray(s?.data) ? s.data : [], venues: Array.isArray(v?.data) ? v.data : [],
    })).catch(() => {});
  }, [open, orgId]);

  const onSave = async (values) => {
    setSaving(true);
    setError('');
    try {
      const row = await saveTeam(orgId, values);
      onCreated?.(row);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Could not save the team');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-slate-100"
        data-testid="add-team-modal">
        <DialogTitle className="sr-only">Add team</DialogTitle>
        {error && <div className="rounded border border-red-500/50 bg-red-900/30 text-red-200 text-sm px-3 py-2" data-testid="add-team-error">{error}</div>}
        {open && (
          <EntityForm entityConfig={ENTITIES.teams} initialValues={initialTitle ? { title: initialTitle } : {}}
            onSave={onSave} onCancel={onClose} saving={saving} entities={entities} entityKey="teams" orgId={orgId} />
        )}
      </DialogContent>
    </Dialog>
  );
}
